/**
 * Tablo kolon özelleştirici — kullanıcı hangi kolonların görüneceğini ve
 * hangi sırada olacağını seçer; tercih localStorage'da saklanır (yenileyince
 * korunur). DnD bağımlılığı eklemeden yukarı/aşağı oklarla sıralama.
 *
 * Kullanım:
 *   const prefs = useColumnPrefs("products.columns", ALL_IDS);
 *   <ColumnCustomizer labels={LABELS} prefs={prefs} />
 *   const visible = prefs.orderedVisibleIds;  // InfiniteTable kolonlarını filtrele/sırala
 */
import {
  ActionIcon, Button, Checkbox, Group, Popover, Stack, Text, Tooltip,
} from "@mantine/core";
import {
  IconColumns, IconChevronUp, IconChevronDown, IconRotate2,
} from "@tabler/icons-react";
import { useCallback, useMemo, useState } from "react";

export interface ColumnPrefs {
  /** Görünür kolonlar, kullanıcının seçtiği sırada. */
  orderedVisibleIds: string[];
  /** Görünür + gizli tüm kolonlar, kayıtlı sırada (özelleştirici listesi). */
  orderedAllIds: string[];
  hidden: Set<string>;
  toggle: (id: string) => void;
  move: (id: string, dir: -1 | 1) => void;
  reset: () => void;
}

interface StoredPref {
  order: string[];
  hidden: string[];
}

function load(key: string): StoredPref | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (Array.isArray(p.order) && Array.isArray(p.hidden)) return p;
  } catch {
    /* bozuk kayıt → yok say */
  }
  return null;
}

function save(key: string, pref: StoredPref): void {
  try {
    localStorage.setItem(key, JSON.stringify(pref));
  } catch {
    /* quota/private mode → sessiz geç */
  }
}

/**
 * Kolon görünürlük + sıra tercihini localStorage'da yönetir.
 *
 * @param storageKey  Benzersiz anahtar (örn. "products.columns")
 * @param allIds      Tüm kolon id'leri (koddaki kanonik sıra)
 * @param lockedIds   Gizlenemeyen/taşınamayan kolonlar (örn. işlem butonları)
 */
export function useColumnPrefs(
  storageKey: string,
  allIds: string[],
  lockedIds: string[] = [],
): ColumnPrefs {
  const locked = useMemo(() => new Set(lockedIds), [lockedIds]);

  const [state, setState] = useState<StoredPref>(() => {
    const stored = load(storageKey);
    if (!stored) return { order: allIds, hidden: [] };
    // Kayıt sonrası kodda yeni kolon eklendiyse: bilinmeyenleri at, yenileri
    // sona ekle → kayıt bozulmadan şema evrilir.
    const known = stored.order.filter((id) => allIds.includes(id));
    const missing = allIds.filter((id) => !known.includes(id));
    return {
      order: [...known, ...missing],
      hidden: stored.hidden.filter((id) => allIds.includes(id)),
    };
  });

  const persist = useCallback((next: StoredPref) => {
    setState(next);
    save(storageKey, next);
  }, [storageKey]);

  const toggle = useCallback((id: string) => {
    if (locked.has(id)) return;
    const hidden = new Set(state.hidden);
    if (hidden.has(id)) hidden.delete(id);
    else hidden.add(id);
    persist({ order: state.order, hidden: [...hidden] });
  }, [state, locked, persist]);

  const move = useCallback((id: string, dir: -1 | 1) => {
    if (locked.has(id)) return;
    const order = [...state.order];
    const i = order.indexOf(id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= order.length) return;
    if (locked.has(order[j])) return;  // kilitli komşuyla yer değiştirme
    [order[i], order[j]] = [order[j], order[i]];
    persist({ order, hidden: state.hidden });
  }, [state, locked, persist]);

  const reset = useCallback(() => {
    persist({ order: allIds, hidden: [] });
  }, [allIds, persist]);

  const hidden = useMemo(() => new Set(state.hidden), [state.hidden]);
  const orderedVisibleIds = useMemo(
    () => state.order.filter((id) => !hidden.has(id)),
    [state.order, hidden],
  );

  return {
    orderedVisibleIds,
    orderedAllIds: state.order,
    hidden,
    toggle,
    move,
    reset,
  };
}

export function ColumnCustomizer({
  labels, prefs, lockedIds = [],
}: {
  /** id → görünen başlık. */
  labels: Record<string, string>;
  prefs: ColumnPrefs;
  lockedIds?: string[];
}) {
  const [opened, setOpened] = useState(false);
  const locked = new Set(lockedIds);

  return (
    <Popover opened={opened} onChange={setOpened} position="bottom-end" withArrow shadow="md">
      <Popover.Target>
        <Tooltip label="Kolonları özelleştir" withinPortal>
          <Button
            variant="default"
            size="sm"
            leftSection={<IconColumns size={16} />}
            onClick={() => setOpened((o) => !o)}
          >
            Kolonlar
          </Button>
        </Tooltip>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap={4} style={{ minWidth: 220 }}>
          <Group justify="space-between" mb={4}>
            <Text size="sm" fw={700}>Kolonlar</Text>
            <Button
              variant="subtle" size="compact-xs"
              leftSection={<IconRotate2 size={13} />}
              onClick={prefs.reset}
            >
              Sıfırla
            </Button>
          </Group>
          {prefs.orderedAllIds.map((id, idx) => {
            const isLocked = locked.has(id);
            return (
              <Group key={id} justify="space-between" wrap="nowrap" gap="xs">
                <Checkbox
                  size="sm"
                  label={labels[id] ?? id}
                  checked={!prefs.hidden.has(id)}
                  disabled={isLocked}
                  onChange={() => prefs.toggle(id)}
                  styles={{ label: { paddingInlineStart: 6 } }}
                />
                <Group gap={2} wrap="nowrap">
                  <ActionIcon
                    variant="subtle" size="sm"
                    disabled={isLocked || idx === 0}
                    onClick={() => prefs.move(id, -1)}
                    aria-label="Yukarı"
                  >
                    <IconChevronUp size={14} />
                  </ActionIcon>
                  <ActionIcon
                    variant="subtle" size="sm"
                    disabled={isLocked || idx === prefs.orderedAllIds.length - 1}
                    onClick={() => prefs.move(id, 1)}
                    aria-label="Aşağı"
                  >
                    <IconChevronDown size={14} />
                  </ActionIcon>
                </Group>
              </Group>
            );
          })}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
